import { OAuthTypeEnum } from "../../../const/oauth.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/internal/userExternalProfiles.ts
const externalProfileSchema = z.object({
	id: z.coerce.bigint(),
	userId: z.coerce.bigint(),
	provider: OAuthTypeEnum,
	externalId: z.string(),
	name: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date()
});
const emailOutcomeSchema = z.enum([
	"set",
	"unchanged",
	"not_requested"
]);
const emailValidationFailureSchema = z.enum([
	"invalid_format",
	"invalid_domain",
	"failed_normalization"
]);
const bindResultSchema = z.discriminatedUnion("status", [
	z.object({
		status: z.literal("created"),
		profile: externalProfileSchema,
		emailOutcome: emailOutcomeSchema
	}),
	z.object({
		status: z.literal("existing"),
		profile: externalProfileSchema,
		emailOutcome: emailOutcomeSchema
	}),
	z.object({ status: z.literal("user_not_found") }),
	z.object({
		status: z.literal("conflict_user_provider"),
		existing: externalProfileSchema
	}),
	z.object({
		status: z.literal("conflict_external_id"),
		existing: externalProfileSchema
	}),
	z.object({
		status: z.literal("email_taken"),
		ownerUserId: z.coerce.bigint()
	}),
	z.object({
		status: z.literal("invalid_email"),
		reason: emailValidationFailureSchema
	})
]);
const bindInputSchema = z.object({
	userId: z.coerce.bigint(),
	provider: OAuthTypeEnum,
	externalId: z.string().min(1),
	name: z.string().optional(),
	email: z.email().optional()
});
const bindBodySchema = z.object({
	profile: externalProfileSchema,
	emailOutcome: emailOutcomeSchema
});
const bindOutputSchema = z.discriminatedUnion("status", [z.object({
	status: z.literal(200),
	body: bindBodySchema
}), z.object({
	status: z.literal(201),
	body: bindBodySchema
})]);
const listByUserInputSchema = z.object({
	userId: z.coerce.bigint(),
	provider: OAuthTypeEnum.optional()
});
const listByUserOutputSchema = z.object({ data: z.array(externalProfileSchema) });
const internalUserExternalProfilesContract = oc.prefix("/internal/user-external-profiles").tag("InternalUserExternalProfiles").router({
	bind: oc.route({
		summary: "Bind a PixAI user to an external account (idempotent + strict)",
		description: "Internal-only. Trusted by Rubert: assumes the external identity has already been verified upstream. Returns 201 when a new binding is created, 200 when an exact match already existed. Conflicts surface as structured 4xx errors. If `email` is provided, may also update the user's email/verified state per the design spec.",
		method: "POST",
		path: "/bind",
		outputStructure: "detailed"
	}).input(bindInputSchema).output(bindOutputSchema).errors({
		FORBIDDEN: {
			status: 403,
			message: "Invalid internal caller"
		},
		USER_NOT_FOUND: {
			status: 404,
			message: "User not found"
		},
		INVALID_EMAIL: {
			status: 400,
			message: "email is invalid or rejected by validation",
			data: z.object({ reason: emailValidationFailureSchema })
		},
		USER_ALREADY_BOUND_TO_PROVIDER: {
			status: 409,
			message: "User has a different externalId for this provider",
			data: z.object({ existingProfileId: z.string() })
		},
		EXTERNAL_ID_BOUND_TO_OTHER_USER: {
			status: 409,
			message: "externalId is bound to a different user",
			data: z.object({ existingUserId: z.string() })
		},
		EMAIL_TAKEN_BY_OTHER_USER: {
			status: 409,
			message: "email is already used by a different user",
			data: z.object({ ownerUserId: z.string() })
		}
	}),
	listByUserId: oc.route({
		summary: "List a user's external profiles (optional provider filter)",
		method: "GET",
		path: "/by-user"
	}).input(listByUserInputSchema).output(listByUserOutputSchema).errors({ FORBIDDEN: {
		status: 403,
		message: "Invalid internal caller"
	} })
});
//#endregion
export { bindBodySchema, bindInputSchema, bindOutputSchema, bindResultSchema, emailOutcomeSchema, emailValidationFailureSchema, externalProfileSchema, internalUserExternalProfilesContract, listByUserInputSchema, listByUserOutputSchema };
